import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const monoFont = "'IBM Plex Mono', monospace";

interface Props {
  userId: string; // user_profiles.id
  onClose: () => void;
  onApplied: () => void;
}

interface Coupon {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  is_active: boolean;
}

export function ApplyCouponModal({ userId, onClose, onApplied }: Props) {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('coupons').select('id, code, description, discount_type, discount_value, is_active').eq('is_active', true).order('created_at', { ascending: false });
      setCoupons((data as any[]) || []);
      setLoading(false);
    };
    load();
  }, []);

  const handleApply = async () => {
    if (!selectedId) return;
    setApplying(true);

    const coupon = coupons.find(c => c.id === selectedId);
    if (!coupon) return;

    // Calculate benefit expiration
    let benefitExpires: string | null = null;
    if (coupon.discount_type === 'months_free') {
      const d = new Date();
      d.setMonth(d.getMonth() + coupon.discount_value);
      benefitExpires = d.toISOString();
    }

    const { error } = await supabase.from('user_coupons').insert({
      user_id: userId,
      coupon_id: selectedId,
      benefit_expires_at: benefitExpires,
    } as any);

    if (error) {
      toast({ title: 'Erro ao aplicar cupom', variant: 'destructive' });
      setApplying(false);
      return;
    }

    // Increment uses_count
    await supabase.rpc('refresh_admin_metrics'); // Just refresh
    const { data: cur } = await supabase.from('coupons').select('uses_count').eq('id', selectedId).single();
    if (cur) {
      await supabase.from('coupons').update({ uses_count: ((cur as any).uses_count || 0) + 1 } as any).eq('id', selectedId);
    }

    toast({ title: `Cupom ${coupon.code} aplicado` });
    onApplied();
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 13, fontWeight: 300,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.7)',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#0A0A0A', border: '0.5px solid rgba(247,246,242,0.12)',
        padding: 24, maxWidth: 400, width: '100%',
      }}>
        <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 20 }}>
          APLICAR CUPOM
        </div>

        {loading ? (
          <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.3)', padding: '20px 0' }}>Carregando cupons...</div>
        ) : coupons.length === 0 ? (
          <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.4)', padding: '20px 0' }}>Nenhum cupom ativo disponível.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
            {coupons.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                style={{
                  textAlign: 'left', padding: 12,
                  border: `0.5px solid ${selectedId === c.id ? '#00E87A' : 'rgba(247,246,242,0.12)'}`,
                  backgroundColor: selectedId === c.id ? 'rgba(0,232,122,0.06)' : 'transparent',
                  cursor: 'pointer', transition: 'all 0.15s ease',
                }}
              >
                <div style={{ fontFamily: monoFont, fontSize: 14, fontWeight: 300, color: '#F7F6F2', letterSpacing: '0.05em' }}>
                  {c.code}
                </div>
                <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', marginTop: 4 }}>
                  {c.discount_type === 'months_free' ? `${c.discount_value} ${c.discount_value === 1 ? 'mês' : 'meses'} grátis` : `${c.discount_value}% desconto`}
                  {c.description ? ` · ${c.description}` : ''}
                </div>
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 16px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent',
            color: 'rgba(247,246,242,0.5)', cursor: 'pointer',
          }}>CANCELAR</button>
          <button onClick={handleApply} disabled={!selectedId || applying} style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 16px', border: '0.5px solid #00E87A', backgroundColor: 'rgba(0,232,122,0.1)',
            color: '#00E87A', cursor: !selectedId || applying ? 'not-allowed' : 'pointer',
            opacity: !selectedId || applying ? 0.5 : 1,
          }}>
            {applying ? 'APLICANDO...' : 'APLICAR'}
          </button>
        </div>
      </div>
    </div>
  );
}
